import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Captures lightweight lifecycle signals to help diagnose reports of the app
 * "freezing" or "closing itself". Events are fanned into
 * `app_lifecycle_telemetry` so admins can correlate symptoms with timing.
 *
 * Events captured:
 *  - session_start          (mount)
 *  - visibility_hidden / visible
 *  - pagehide              (likely tab eviction on iOS Safari)
 *  - freeze / resume       (Chrome/Safari Page Lifecycle API)
 *  - unhandled_error
 *  - unhandled_rejection
 *  - low_memory             (when navigator.deviceMemory <= 2 at boot)
 */
export function useLifecycleTelemetry() {
  const sessionIdRef = useRef<string>(
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );
  const lastVisibleAtRef = useRef<number>(Date.now());

  useEffect(() => {
    const sessionId = sessionIdRef.current;
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : null;

    const send = (event_type: string, details: Record<string, unknown> = {}) => {
      try {
        const payload = {
          event_type,
          details: {
            session_id: sessionId,
            ts_iso: new Date().toISOString(),
            ...details,
          },
          user_agent: ua,
          url: typeof window !== "undefined" ? window.location.pathname : null,
        };

        // Use sendBeacon when available so events fire during pagehide/unload.
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
        const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

        // Resolve user_id from current session if available (best-effort, sync from cache).
        void supabase.auth.getSession().then(({ data }) => {
          const userId = data.session?.user?.id ?? null;
          const accessToken = data.session?.access_token ?? SUPABASE_KEY;
          const body = JSON.stringify({ ...payload, user_id: userId });

          // Always prefer keepalive fetch + sendBeacon fallback. On low-memory
          // Android, Chrome may kill the renderer at any time (not just on
          // pagehide), so async supabase-js inserts often never flush.
          if (
            typeof navigator !== "undefined" &&
            "sendBeacon" in navigator &&
            SUPABASE_URL
          ) {
            try {
              const blob = new Blob([body], { type: "application/json" });
              const url = `${SUPABASE_URL}/rest/v1/app_lifecycle_telemetry`;
              const ok = fetch(url, {
                method: "POST",
                keepalive: true,
                headers: {
                  "Content-Type": "application/json",
                  apikey: SUPABASE_KEY,
                  Authorization: `Bearer ${accessToken}`,
                  Prefer: "return=minimal",
                },
                body,
              }).catch(() => {});
              void ok;
              // Fire beacon as last-resort backup for terminal events. beacon
              // payloads survive renderer death; keepalive fetch may not.
              if (event_type === "pagehide" || event_type === "freeze" || event_type === "unhandled_error") {
                navigator.sendBeacon(url, blob);
              }
              return;
            } catch {
              /* fall through to standard insert */
            }
          }

          void supabase
            .from("app_lifecycle_telemetry")
            .insert({ ...payload, user_id: userId })
            .then(() => {});
        });
      } catch {
        /* never throw from telemetry */
      }
    };

    // Boot event with environment context
    const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
    const isStandalone =
      typeof window !== "undefined" &&
      (window.matchMedia?.("(display-mode: standalone)").matches ||
        (navigator as Navigator & { standalone?: boolean }).standalone === true);

    send("session_start", {
      device_memory: deviceMemory ?? null,
      hardware_concurrency: navigator.hardwareConcurrency ?? null,
      is_standalone: isStandalone,
      viewport: {
        w: window.innerWidth,
        h: window.innerHeight,
        dpr: window.devicePixelRatio,
      },
    });

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        send("visibility_hidden", {
          visible_ms: Date.now() - lastVisibleAtRef.current,
        });
      } else {
        const now = Date.now();
        send("visibility_visible", {
          hidden_ms: now - lastVisibleAtRef.current,
        });
        lastVisibleAtRef.current = now;
      }
    };

    const onPageHide = (e: PageTransitionEvent) => {
      send("pagehide", { persisted: e.persisted });
    };

    const onFreeze = () => send("freeze", {});
    const onResume = () => send("resume", {});

    const onError = (e: ErrorEvent) => {
      send("unhandled_error", {
        message: e.message,
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
      });
    };

    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      const message =
        reason instanceof Error
          ? reason.message
          : typeof reason === "string"
            ? reason
            : JSON.stringify(reason)?.slice(0, 500);

      // Filter benign noise:
      // 1. Safari/iOS fires this when a tab/PWA is closed while a fetch is in flight.
      // 2. Empty {} rejections are almost certainly aborted fetches during navigation.
      if (
        /browsing context is going away/i.test(message) ||
        message === "{}"
      ) {
        return;
      }

      send("unhandled_rejection", { message });
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("freeze", onFreeze as EventListener);
    document.addEventListener("resume", onResume as EventListener);
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("freeze", onFreeze as EventListener);
      document.removeEventListener("resume", onResume as EventListener);
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
}